// src/components/Apm/BankAccount/BankAccount.js

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import { batch, useDispatch, useSelector } from 'react-redux';

import { Form, Formik, useFormikContext } from 'formik';

import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import { Container, Grid, useMediaQuery, useTheme } from '@mui/material';

import { addOrEditItem, deleteBankAccount } from '../../../redux/slices/bankAccountSlice';

import { setFormSimpleTouched } from '../../../redux/slices/formTouchedSlice';

import { setItemForEditBasic } from '../../../redux/slices/itemForEditBasicSlice';

import { setLoadingStatus } from '../../../redux/slices/loadingStatusSlice';

import { setPopup } from '../../../redux/slices/popupSlice';

import { setPopupSimple } from '../../../redux/slices/popupSimpleSlice';

import { bankAccountURL } from '../../Data/baseURL';

import ButtonsContainer from '../../Controls/Buttons/ButtonsContainer';

import { initialValuesBankAccount } from '../Tables/InitialValues/initialValuesBankAccount';

import TextFieldNormal from '../../Controls/Inputs/TextFieldNormal';
import axiosInstance from '../../../services/axios/axios';
import AutocompleteWrapper from '../../Controls/FormsUI/AutocompleteWrapper';

const validationSchema = Yup.object({
  currency_code: Yup.object()
    .shape()
    .typeError(() => i18n.t('yup.required'))
    .required(() => i18n.t('yup.required')),

  iban_number: Yup.string()
    .required(() => i18n.t('yup.required'))
    .max(50, () => i18n.t('yup.max_50_characters')),

  bank_name: Yup.string()
    .required(() => i18n.t('yup.required'))
    .max(150, () => i18n.t('yup.max_150_characters')),

  bank_code: Yup.string()
    .required(() => i18n.t('yup.required'))
    .max(30, () => i18n.t('yup.max_30_characters')),

  bank_address: Yup.string()
    .max(250, () => i18n.t('yup.max_250_characters'))
    .nullable(),
});

const getBankAccountIdentifier = (bankAccount) => {
  return bankAccount?.uf ?? bankAccount?.id ?? null;
};

const normalizeNullableString = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).trim();

  return normalized || null;
};

const buildBankAccountPayload = (values) => {
  return {
    currency_code: values.currency_code ?? values.currencycode ?? null,

    iban_number: values.iban_number?.trim() ?? '',

    bank_name: values.bank_name?.trim() ?? '',

    bank_code: values.bank_code?.trim() ?? '',

    bank_address: normalizeNullableString(values.bank_address),

    contact: values.contact?.uf ?? values.contact?.id ?? values.contact ?? null,
  };
};

const getErrorStatus = (error) => {
  return error?.response?.status ?? error?.status ?? error?.originalStatus;
};

const FormTouchedObserver = () => {
  const dispatch = useDispatch();

  const { touched } = useFormikContext();

  useEffect(() => {
    dispatch(setFormSimpleTouched(Object.keys(touched).length > 0));
  }, [dispatch, touched]);

  return null;
};

const BankAccount = forwardRef(({ addOrEdit, onDeleteAutoField }, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const theme = useTheme();

  const lgMediaDown = useMediaQuery(theme.breakpoints.down('lg'));

  const formikRef = useRef(null);

  const { itemForEditBasic } = useSelector((state) => state.itemForEditBasicSlice);

  const formInitialValues = useMemo(
    () => ({
      ...initialValuesBankAccount,
      ...(itemForEditBasic ?? {}),

      currency_code: itemForEditBasic?.currency_code ?? itemForEditBasic?.currencycode ?? null,

      iban_number: itemForEditBasic?.iban_number ?? '',

      bank_name: itemForEditBasic?.bank_name ?? '',

      bank_code: itemForEditBasic?.bank_code ?? '',

      bank_address: itemForEditBasic?.bank_address ?? '',
    }),
    [itemForEditBasic]
  );

  useImperativeHandle(
    ref,
    () => ({
      handleSubmit: () => {
        return formikRef.current?.submitForm();
      },
    }),
    []
  );

  const handleSubmit = async (values, { setSubmitting }) => {
    console.log('LOG_1118', values);

    const fieldName = values?.fieldName ?? itemForEditBasic?.fieldName;

    const isEmbeddedMode = Boolean(fieldName);

    /*
     * Embedded mode:
     *
     * The bank account is edited inside Contact.
     * It should be returned to the Contact form and
     * saved together with the parent contact.
     */
    if (isEmbeddedMode) {
      try {
        addOrEdit?.({
          ...values,
          fieldName,
          index: values?.index ?? itemForEditBasic?.index ?? null,
        });

        batch(() => {
          dispatch(setFormSimpleTouched(false));

          dispatch(setPopupSimple(false));

          dispatch(setItemForEditBasic(null));
        });
      } finally {
        setSubmitting(false);
      }

      return;
    }

    const identifier = getBankAccountIdentifier(values);

    const isEditing = Boolean(identifier);

    batch(() => {
      dispatch(setLoadingStatus(true));

      dispatch(setItemForEditBasic(values));
    });

    try {
      const response = await axiosInstance({
        method: isEditing ? 'PUT' : 'POST',

        url: isEditing ? `${bankAccountURL}${identifier}/` : bankAccountURL,

        data: buildBankAccountPayload(values),
      });

      if (!response?.data) {
        return;
      }

      batch(() => {
        dispatch(setFormSimpleTouched(false));

        dispatch(
          addOrEditItem({
            ...response.data,

            fieldName: itemForEditBasic?.fieldName ?? null,

            index: itemForEditBasic?.index ?? null,
          })
        );
      });

      addOrEdit?.(response.data);

      toast.success(t(isEditing ? 'toast.record_successfully_updated' : 'toast.record_successfully_created'));
    } catch (error) {
      console.error('BANK_ACCOUNT_SAVE_ERROR', error);

      const status = getErrorStatus(error);

      if (status === 404) {
        toast.error(t('toast.record_not_found'));

        return;
      }

      if (status === 409) {
        toast.error(t('toast.record_should_be_unique'));

        return;
      }

      if (status === 405) {
        toast.error(
          t('toast.method_not_allowed', {
            defaultValue: 'The requested update method is not allowed.',
          })
        );

        return;
      }

      toast.error(t('toast.record_could_not_be_created_or_updated'));
    } finally {
      dispatch(setLoadingStatus(false));

      setSubmitting(false);
    }
  };

  const handleDelete = async (values) => {
    const identifier = getBankAccountIdentifier(values);

    const fieldName = values?.fieldName ?? itemForEditBasic?.fieldName;

    const index = values?.index ?? itemForEditBasic?.index ?? null;

    const isEmbeddedMode = Boolean(fieldName);

    /*
     * Embedded bank account:
     * remove it only from the Contact form array.
     */
    if (isEmbeddedMode) {
      if (typeof onDeleteAutoField !== 'function' || index === null) {
        toast.error(t('toast.record_not_found'));

        return;
      }

      onDeleteAutoField(identifier, index);

      batch(() => {
        dispatch(setFormSimpleTouched(false));

        dispatch(setPopupSimple(false));

        dispatch(setItemForEditBasic(null));
      });

      return;
    }

    if (!identifier) {
      toast.error(t('toast.record_not_found'));

      return;
    }

    dispatch(setLoadingStatus(true));

    try {
      const response = await axiosInstance({
        method: 'DELETE',

        url: `${bankAccountURL} ${identifier}/`,
      });

      batch(() => {
        dispatch(deleteBankAccount(identifier));

        dispatch(setPopup(false));

        dispatch(setFormSimpleTouched(false));

        dispatch(setItemForEditBasic(null));
      });

      if (response.status === 204) {
        toast.success(t('toast.record_successfully_deleted'));
      }
    } catch (error) {
      console.error('BANK_ACCOUNT_DELETE_ERROR', error);

      const status = getErrorStatus(error);

      if (status === 404) {
        toast.error(t('toast.record_not_found'));

        return;
      }

      if (status === 409) {
        toast.error(t('toast.record_not_allowed_be_deleted'));

        return;
      }

      toast.error(t('toast.record_could_not_be_created_or_updated'));
    } finally {
      dispatch(setLoadingStatus(false));
    }
  };

  return (
    <Container maxWidth="md" disableGutters={lgMediaDown}>
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur
        onSubmit={handleSubmit}
      >
        {({ values, errors }) => {
          console.log('LOG_1138_VALUES', errors);
          return (
            <Form autoComplete="off">
              <Grid
                container
                item
                spacing={2}
                sx={{
                  mt: 3,
                  mb: 5,
                }}
              >
                <Grid item md={6} xs={12}>
                  <AutocompleteWrapper name="currency_code" label={t('column.currency_code')} required />
                </Grid>

                <Grid item md={6} xs={12}>
                  <TextFieldNormal name="iban_number" label={t('column.iban')} required />
                </Grid>

                <Grid item md={6} xs={12}>
                  <TextFieldNormal name="bank_name" label={t('column.bank_name')} required />
                </Grid>

                <Grid item md={6} xs={12}>
                  <TextFieldNormal name="bank_code" label={t('column.bank_code')} required />
                </Grid>

                <Grid item xs={12}>
                  <TextFieldNormal name="bank_address" label={t('bank_account.bank_address_optional')} />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{
                    mt: 3,
                  }}
                >
                  <ButtonsContainer
                    values={values}
                    handleDelete={(_event, _identifier, currentValues) => handleDelete(currentValues)}
                  />
                </Grid>
              </Grid>

              <FormTouchedObserver />
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
});

BankAccount.displayName = 'BankAccount';

export default BankAccount;
